// Kept out of hooks.ts because it imports the well-page-content index, which
// starts requests as it loads. utils.ts imports hooks.ts, and the tests of
// utils.ts mock `lib/request`.
import { useCallback, useEffect } from 'react';
import { useAtom, useSetAtom } from 'jotai';
import {
  dcaScenariosAtom,
  defaultDcaScenario,
  selectedDcaScenarioAtom,
} from 'components/well-page-content';
import { useSeedDcaScenario } from 'lib/dca-scenarios';
import { useLatest } from 'lib/hooks/use-latest';
import type { DcaScenariosResponse } from 'types/api-responses';

/**
 * Select the DCA scenario a DCA workspace opens on: its saved scenario, or
 * "pdp default" when it has none or the saved one can't be read. Also loads
 * the scenario list for the page's scenario dropdown.
 *
 * @param {number | null | undefined} savedScenarioId - The workspace's saved DCA scenario.
 * @param {number} workspaceId - The workspace shown.
 * @return {void}
 */
export function useWorkspaceDcaScenario(
  savedScenarioId: number | null | undefined,
  workspaceId: number
): void {
  const [dcaScenarios, fetchDcaScenarios] = useAtom(dcaScenariosAtom);
  const setSelectedScenario = useSetAtom(selectedDcaScenarioAtom);
  const { isError: savedScenarioFailed } = useSeedDcaScenario(
    savedScenarioId,
    workspaceId
  );

  const startsOnDefault =
    savedScenarioId === null ||
    savedScenarioId === undefined ||
    savedScenarioFailed;
  // Read inside callbacks without rerunning them when they change.
  const startsOnDefaultRef = useLatest(startsOnDefault);
  const dcaScenariosResultRef = useLatest(dcaScenarios.result);

  const selectDefaultScenario = useCallback(
    (scenarios: DcaScenariosResponse[]) => {
      const scenario = defaultDcaScenario(scenarios);
      if (scenario) setSelectedScenario(scenario);
    },
    [setSelectedScenario]
  );

  // The saved scenario failed to load after the list had loaded.
  useEffect(() => {
    if (!savedScenarioFailed) return;
    const result = dcaScenariosResultRef.current;
    if (result?.type === 'success') selectDefaultScenario(result.data.results);
  }, [dcaScenariosResultRef, savedScenarioFailed, selectDefaultScenario]);

  useEffect(() => {
    fetchDcaScenarios({
      url: '/queries/reservoir/dca_scenarios',
      type: 'POST',
      data: {
        query: {
          page: 1,
          pageSize: 50,
          sort_attribute: 'created_at',
          sort_direction: 'DESC',
        },
      },
      onSuccess: (data) => {
        if (startsOnDefaultRef.current) selectDefaultScenario(data.results);
      },
    });
  }, [fetchDcaScenarios, selectDefaultScenario, startsOnDefaultRef]);
}
